'use client';

import Image from 'next/image';
import { useRouter } from '@/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { ImageCouldFromRemoteServer as RemoteServerImage } from '@/components/images'
import type { NavItems } from '@/types/nav';
import { Maybe, matchEquals } from '@nf/utils-common/fpw/monadic';
import { uiService } from '@/services/ui.service';
import { Icon, SportIcon, ArrowDownIcon, NewWindowIcon, FilterLiveIcon } from '@/components/icons/svg-icons';
import { SimpleAccordionDetails, Button } from '@nf/ui';
import { Tip } from './tip';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { menuIconStyle } from './sidebar-item.style';
import { cx, css } from 'styled-system/css';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useSiteStore } from '@/store/site';
import { useAuthToken } from '@/store/auth';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo, useAccountStatus } from '@/hooks/query/account';
import { openCasinoRoyaleInNewWindow } from '@/hooks/query/use-casino-royale';
import { AccountActStatus, KafkaActionSettings, KafkaMarsTypeEnums, KafkaNote } from '@nf/utils-common/constants';
import { isVirtualSports } from '@/services/settings.service';
import { useSelectedLayoutSegments } from 'next/navigation';
import { openESport } from '@/services/third-party-entrance.service';
import { openExchangeInNewWindow } from '@/hooks/query';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { TimeSensitiveNewTag } from '@/features/new-tag';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Tooltip } from '@/components/widgets/tooltip';
import { modalService } from '@/services/modal.service';
import { goldenDuelStorageService } from '@/services/golden-duel/golden-duel-storage.service';

interface SidebarItemProps {
	item: NavItems[0];
	clickThenNavigate?: boolean;
	children: React.ReactNode;
}

const nextAnalyseHrefKeyMapping = {
	sports: 'switch-to-odds-page-via-left-menu',
	world: 'switch-to-odds-page-via-left-menu',
	live: 'switch-to-odds-page-via-left-menu',
	bets: 'switch-to-statement-via-left-menu',
	lobby: 'switch-to-lobby-via-left-menu',
	promotion: 'switch-to-promotion-via-left-menu',
	favorites: 'switch-to-my-favorites-via-left-menu'
};

const getNextAnalyseKey = (itemHref: string): string => {
	return Object.entries(nextAnalyseHrefKeyMapping).reduce((analyseKey, mapping): string => {
		const [href, key] = mapping;
		if (!analyseKey && itemHref.startsWith(`/${href}`)) {
			analyseKey = key;
		}
		return analyseKey;
	}, '');
};

/**
 * sports that need to be analyzed by catcher
 */
const analyseSports = ['1', '2', '5', '6', '8', '43', '50', '179'];

export const SidebarItem = ({
	item,
	clickThenNavigate,
	children
}: SidebarItemProps) => {
	const router = useRouter();
	const locale = useLocale();
	const hasSubItems = Boolean(item.subItems?.length);
	const [
		isParlayMode,
		setIsParlayMode,
		setIsOpenExchange,
		deepLinkSiteInfo
	] = useSiteStore(state => [
		state.isParlayMode,
		state.setIsParlayMode,
		state.setIsOpenExchange,
		state.deepLinkSiteInfo
	]);
	const view = useAppearanceStore(state => state.view);
	const resetAllFilter = useSiteStore(state => state.resetAllFilter);
	const authToken = useAuthToken();
	const isBefore = !Boolean(authToken);
	const visitorInfo = useAtomValue(visitorAtom);
	const beforeToken = visitorInfo?.AccessToken;
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const t = useTranslations('General');
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const pathSegments = selectedLayoutSegments.map(segment => `/${segment}`);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	let nextAnalyseKey = getNextAnalyseKey(item.href);

	if (item.href.startsWith('/sports/') && analyseSports.includes(item.sportId ?? '')) {
		nextAnalyseKey = `${nextAnalyseKey}-${item.sportId}`;
	}

	const taggedElement = useAnalyseAttribute(nextAnalyseKey);
	const [needShowGoldenDuelTooltip, setNeedShowGoldenDuelTooltip] = useState(true);
	const analyseStreamersRef = useAnalyseAttribute('open-streamers-list-button-from-menu');
	const { data: actStatus } = useAccountStatus();

	const setKafkaGalaxy = () => {
		if (item.href.includes('streamer')) {
			const isMobileMode = window.innerWidth < 750;
			setKafkaNormal({
				note: KafkaNote.GalaxyV2,
				type: 13,
				action: isMobileMode ? 6 : 9,
			});
			return;
		}

		if (isVirtualSports(Number(item.sportId))) {
			setKafkaNormal({
				note: KafkaNote.GalaxyV2,
				type: 15,
				action: 1
			});
			return;
		}

		let action = {
			'/lobby': 11, // home
			'/promotion': 12, // promotion
			'/bets/u': 2, // my bets
			'/search': 3, // serach
			'/favorites': 4, // my fav
			'/section/ph': 13, // PH
		}[item.href];

		if (!action) {
			if (item.href.includes('olympics')) {
				action = 5;
			} else if (item.href.includes('euro-cup') || item.href.includes('womens-euro') || item.href.includes('nations-league')) {
				action = 6;
			} else if (item.href.includes('copa-america')) {
				action = 7;
			} else if (item.href.includes('asian-cup')) {
				action = 8;
			}
		}

		if (action) {
			const isMobileMode = window.innerWidth < 750;
			const kafkaData = {
				note: KafkaNote.GalaxyV2,
				type: isMobileMode ? 4 : 5,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMars = () => {
		const isMobileMode = window.innerWidth < 750;

		const action = {
			'/lobby': isMobileMode ? 6 : 14, // home
			'/promotion': isMobileMode ? 7 : 15, // promotion
			'/favorites': isMobileMode ? 8 : 16, // my fav
			'/bets/u': isMobileMode ? 9 : 17, // statement
			'/streamer': isMobileMode ? 6 : 10 // streamer
		}[item.href];
		if (isMobileMode && (item.sportId === '1' || item.sportId === '2')) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 4,
				action: item.sportId === '1' ? 24 : 25
			}
			setKafkaNormal(kafkaData);
		}
		if (action) {
			const isStreamer = item.href.includes('streamer');
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: isStreamer ? 5 : 4,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMarsHome = () => {
		const isHomePage = pathSegments.includes('/lobby');
		const isMobileMode = window.innerWidth < 750;
		if (!isHomePage || isMobileMode) return; // 只計 mars home page 且寬度 750 以上

		let action = {
			'/lobby': 16, // home
			'/promotion': 17, // promotion
			'/favorites': 18, // my fav
			'/bets/u': 19, // statement
		}[item.href];

		if (!action) {
			if (item.href.includes('euro-cup') && item.sportId === '1') {
				action = 5; // euro-cup > soccer
			} else if (item.href.includes('/sports') && item.sportId === '1' && !isParlayMode) {
				action = 22; // sports > soccer
			}
		}

		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMarsPinGoal = () => {
		const pinGoalActions = KafkaActionSettings.MarsV1[KafkaMarsTypeEnums.PinGoal];
		const action = {
			'9995': pinGoalActions.PinGoalSoccer,
			'9996': pinGoalActions.PinGoalBasketball,
			'9997': pinGoalActions.PinGoalEsport,
		}[item.sportId ?? ''];
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: KafkaMarsTypeEnums.PinGoal,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafka = () => {
		if (view === ViewEnum.galaxy) {
			setKafkaGalaxy();
		} else if (view === ViewEnum.mars) {
			setKafkaMars();
			setKafkaMarsHome();
			setKafkaMarsPinGoal();
		}
	}

	const setEsportsWebKafka = () => {
		const isMobileMode = window.innerWidth < 750;
		const isGalaxy = view === ViewEnum.galaxy;
		const action = {
			'/esportsWeb': isGalaxy ? 1 : 2,
		}[item.href];

		if (action) {
			const kafkaData = {
				note: KafkaNote.EsportsWeb,
				type: isMobileMode ? 15 : 16,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const navigateToLink = (event: React.MouseEvent) => {
		event.stopPropagation();
		setKafka();
		uiService.controlSidebar(false);

		if (item.href === '/esportsWeb' && user?.EnableESportWeb && SiteConfigsData) {
			const esportsData = {
				siteId: SiteConfigsData.GetMessageID,
				umSource: t('lbl_GameUnderMaintain'),
				isApp: deepLinkSiteInfo.isApp
			};
			setEsportsWebKafka();
			openESport(esportsData, isBefore ? beforeToken : authToken);
			return;
		}
		if (item.href === '/exchange') {
			openExchangeInNewWindow(isBefore, user, SiteConfigsData, t('lbl_deposit'));
			return;
		}
		if ((item.href.includes('world') || item.href.includes('favorites') || isVirtualSports(Number(item.href.split('/')[2]))) && isParlayMode) {
			setIsParlayMode(false)
		}
		if (item.href === '/third-party/casino-royale' && user && SiteConfigsData) {
			openCasinoRoyaleInNewWindow(user, SiteConfigsData, deepLinkSiteInfo.isApp);
			return;
		}
		if (item.href.includes('game-lobby') && user && SiteConfigsData) {
			const params = new URLSearchParams(item.href.split('?')[1]);
			const sportId = params.get('sid');
			const category = params.get('cid');
			const betTypeId = params.get('bid');
			openCasinoRoyaleInNewWindow(user, SiteConfigsData, deepLinkSiteInfo.isApp, sportId, betTypeId, category);
			return;
		}

		resetAllFilter();
		router.push(item.href, { locale });
	};

	const subItems = item.subItems?.map(subItem => {
		if (subItem.sportId === '999') return
		return (
			<SidebarItem
				key={subItem.href}
				item={subItem}
			>
				{subItem.title}
			</SidebarItem>
		)
	}) ?? [];

	const toggleStreamer = (sportId?: string) => {
		if (sportId) {
			uiService.controlSidebar(false);
			router.push('/streamer');
			const width = window.innerWidth;
			const kafkaData = {
				note: (view === ViewEnum.mars) ? KafkaNote.MarsV1 : KafkaNote.GalaxyV2,
				type: (view === ViewEnum.mars) ? 5 : 13,
				action: width > 750 ? 4 : 3
			}
			setKafkaNormal(kafkaData);
		}
	}

	useEffect(() => {
		const subscription = modalService.onOpenGoldenDuelToolTipModal.subscribe((isOpen) => {
			if (isNeedShowGoldenDuelTooltip()) {
				setIsOpen(isOpen);
			}
		});

		const isNeedShowGoldenDuelTooltip = () => {
			return SiteConfigsData?.EnableGoldenDuel && needShowGoldenDuelTooltip;
		}

		return () => subscription.unsubscribe();
	}, []);

	return (
		<>
			<div
				className={item.className}
				onClick={(!hasSubItems || clickThenNavigate) ? navigateToLink : setKafka}
				data-selected={item.selected}
				ref={taggedElement}
			>
				{item.icon !== '' && Maybe(item.icon).fold(
					() => null,
					(icon: string) => {
						return matchEquals(item.iconType)
							.on('general', () => <Icon name={icon} />)
							.on('sport', () => (
								<div className={menuIconStyle.menuIcon}>
									<SportIcon
										sportId={icon}
										isColorful={view === ViewEnum.galaxy ? true : item.selected}
									/>
									{view === ViewEnum.mars && item.hasLive ? (
										<div className={cx(menuIconStyle.menuLiveIcon, css({ display: 'flex' }))}>
											<FilterLiveIcon />
										</div>
									) : null}
								</div>
							))
							.on('image', () => (
								<Image
									src={icon}
									alt={item.title}
									width={24}
									height={24}
								/>
							))
							.otherwise(() => null);
					}
				)}
				<div className="text">{children}</div>
				{isOpen && item.goldTipText ? (
					<div className={clsx(menuIconStyle.tip, 'tip')} data-open={needShowGoldenDuelTooltip}>
						<div className="scrollbar scrollbar--tip">
							<Tooltip
								message={item.goldTipText}
								className="tip__content"
								countdownTime={5000}
								showMessageDoAction={() => goldenDuelStorageService.markTooltipAsShown()}
								clearMessage={() => setNeedShowGoldenDuelTooltip(false)}
							/>
						</div>
					</div>
				) : null}
				{item.useTip ? <Tip className="tip" auto={true} /> : null}

				{item.amount && item.amount > 0 ? (
					<div className="badge badge--default">{item.amount}</div>
				) : null}
				{(item.sportId !== undefined && ['177', '198'].includes(item.sportId)) ? <TimeSensitiveNewTag className={'new-tag'} /> : null}

				{
					!isBefore && actStatus === AccountActStatus.Deposited && SiteConfigsData?.EnableStreamerSchedule && item.hasStreamer ?
						(
							<Button
								ref={analyseStreamersRef}
								// data-active={streamerSportId.toString() === item?.sportId}
								variant={'icon'}
								onClick={(e) => {
									e.stopPropagation();
									toggleStreamer(item?.sportId);
								}}
							>
								<RemoteServerImage
									src="/images/live-streamer/float-streamer.png"
									alt=""
									width="21"
									height="21"
									isGalaxyFolder={true}
								/>
							</Button>
						) : null
				}

				{item.isNewWindow ? <NewWindowIcon /> : null}

				{item.isRedirectToProduct ? <Icon name="arrow-right" className={menuIconStyle.rightIcon} /> : null}
				{hasSubItems ? <ArrowDownIcon /> : null}
			</div>

			{hasSubItems ? (
				<SimpleAccordionDetails className="side-sub-menu">
					{subItems}
				</SimpleAccordionDetails>
			) : null}
		</>
	);
};
